(ns app.team.page
  "The UI that allows to manage the team members (users) and define who should
   receive an email whenever an Instagram disconnects from the team /
   organization."
  (:require [app.team.validation :as validation]
            [parts.replicant.query :as query]
            [parts.replicant.command :as command]
            [ui.app-frame :as app-frame]))

(def team-query
  {:query/kind :query/team})

(def refresh-action
  [:data/query {:query/kind :query/team}])

(defn- section-card
  [{:keys [title description]} children]
  [:div
   {:class ["bg-white" "rounded-xl" "shadow-sm" "border" "border-gray-200"
            "overflow-hidden"]}
   [:div
    {:class ["px-5" "py-4" "border-b" "border-gray-100"]}
    [:h2
     {:class ["text-lg" "font-semibold" "text-gray-900"]}
     title]
    (when description
      [:p
       {:class ["text-sm" "text-gray-500" "mt-0.5"]}
       description])]
   (into [:div {:class ["divide-y" "divide-gray-100"]}]
         children)])

(defn- team-name-section
  [state]
  (let [result (query/get-result state team-query)
        current-name (:organization/name result)
        new-name (::team-name-input state)
        cmd {:command/kind :command/team-set-name
             :command/data {:name new-name}}
        valid-name? (and new-name (validation/valid-team-name? new-name))
        has-changes? (and new-name (not= new-name current-name))]
    [:div
     {:class ["px-5" "py-4" "flex" "items-center" "justify-between" "gap-4"]}
     [:div
      {:class ["min-w-0" "flex-1"]}
      [:div
       {:class ["flex" "flex-col" "sm:flex-row" "gap-3" "items-start" "sm:items-center"]}
       [:input
        {:type "text"
         :value (or new-name current-name)
         :class ["block" "w-full" "sm:max-w-sm" "rounded-lg" "border" "border-gray-300"
                 "px-3" "py-2" "text-sm" "text-gray-900" "shadow-sm"
                 "placeholder:text-gray-400"
                 "focus:ring-2" "focus:ring-indigo-600" "focus:border-indigo-600"]
         :placeholder "Enter team name"
         :on {:input [[:store/assoc ::team-name-input :event/target.value]]}}]
       (when has-changes?
         [:button
          {:class ["inline-flex" "items-center" "px-4" "py-2" "text-sm"
                   "font-medium" "rounded-lg" "text-white" "bg-indigo-600"
                   "hover:bg-indigo-700" "cursor-pointer" "shrink-0"
                   "disabled:bg-gray-300" "disabled:cursor-not-allowed"]
           :disabled (or (command/issued? state cmd)
                         (not valid-name?))
           :on {:click [[:data/command cmd
                         {:on-success [refresh-action]
                          :on-error [[:ui/show-error]]}]]}}
          "Save"])]
      (when (and new-name (not valid-name?))
        [:p {:class ["text-red-500" "text-sm" "mt-2"]}
         validation/team-name-error-message])
      (when (command/error? state cmd)
        [:p {:class ["text-red-500" "text-sm" "mt-2"]}
         (command/get-latest-error state cmd)])]]))

(defn- member-row
  [state user]
  (let [current-email (:user/email
                         (query/get-result state
                                           {:query/kind :query/user-session}))
        is-self (= (:user/email user) current-email)
        remove-cmd {:command/kind :command/team-remove-user
                    :command/data {:email (:user/email user)}}]
    [:div
     {:class ["px-5" "py-3" "flex" "items-center" "justify-between" "gap-3"]
      :replicant/key (:user/email user)}
     [:div
      {:class ["min-w-0" "flex-1"]}
      [:div
       {:class ["text-sm" "text-gray-900" "truncate"]}
       (:user/email user)]
      (when is-self
        [:div
         {:class ["text-xs" "text-gray-400"]}
         "You"])]
     (if is-self
       [:span
        {:class ["text-xs" "text-gray-300"]
         :title "You cannot remove yourself"}
        "Remove"]
       [:button
        {:class ["text-sm" "font-medium" "text-red-600" "hover:text-red-500"
                 "cursor-pointer" "shrink-0"]
         :on {:click [[:js/confirm (str "Remove " (:user/email user) " from the team?")]
                      [:data/command remove-cmd
                       {:on-success [refresh-action]
                        :on-error [[:ui/show-error]]}]]}}
        "Remove"])]))

(defn- members-section
  [state]
  (let [result (query/get-result state team-query)
        users (sort-by :user/email (:users result))]
    (section-card
      {:title "Team Members"
       :description (str (count users)
                         (if (= 1 (count users)) " member" " members"))}
      (mapv (fn [user] (member-row state user))
            users))))

(defn- invitation-row
  [invitation]
  (let [revoke-cmd {:command/kind :command/team-revoke-invitation
                    :command/data invitation}]
    [:div
     {:class ["px-5" "py-3" "flex" "items-center" "justify-between" "gap-3"]
      :replicant/key (:id invitation)}
     [:div
      {:class ["min-w-0" "flex-1"]}
      [:div
       {:class ["text-sm" "text-gray-900" "truncate"]}
       (:email invitation)]
      [:div
       {:class ["text-xs" "text-amber-600"]}
       "Pending"]]
     [:button
      {:class ["text-sm" "font-medium" "text-red-600" "hover:text-red-500"
               "cursor-pointer" "shrink-0"]
       :on {:click [[:js/confirm (str "Revoke invitation for " (:email invitation) "?")]
                    [:data/command revoke-cmd
                     {:on-success [refresh-action]
                      :on-error [[:ui/show-error]]}]]}}
      "Revoke"]]))

(defn- invitations-section
  [state]
  (let [result (query/get-result state team-query)
        invitations (sort-by :email (:invitations result))]
    (when (seq invitations)
      (section-card
        {:title "Pending Invitations"
         :description (str (count invitations) " pending")}
        (mapv invitation-row invitations)))))

(defn- disconnect-warning-section
  [state]
  (let [result (query/get-result state team-query)
        receivers (:disconnect-warning-receivers result)]
    (when (seq receivers)
      (section-card
        {:title "Disconnect Warning Emails"
         :description "Notify when an Instagram account gets disconnected"}
        (mapv
          (fn [[email receiver?]]
            (let [cmd {:command/kind :command/team-toggle-disconnect-warning-receiver
                       :command/data {:email email}}]
              [:div
               {:class ["px-5" "py-3" "flex" "items-center" "justify-between" "gap-3"]
                :replicant/key email}
               [:div
                {:class ["text-sm" "text-gray-900" "truncate" "min-w-0"]}
                email]
               [:button
                {:class (into ["relative" "inline-flex" "h-6" "w-11" "shrink-0"
                               "cursor-pointer" "rounded-full" "border-2"
                               "border-transparent" "transition-colors" "duration-200"]
                              (if receiver?
                                ["bg-indigo-600"]
                                ["bg-gray-200"]))
                 :role "switch"
                 :aria-checked (str (boolean receiver?))
                 :disabled (command/issued? state cmd)
                 :on {:click [[:data/command cmd
                               {:on-success [refresh-action]
                                :on-error [[:ui/show-error]]}]]}}
                [:span
                 {:class (into ["pointer-events-none" "inline-block" "h-5" "w-5"
                                "rounded-full" "bg-white" "shadow" "ring-0"
                                "transition" "duration-200"]
                               (if receiver?
                                 ["translate-x-5"]
                                 ["translate-x-0"]))}]]]))
          (sort-by first receivers))))))

(defn render [state]
  (let [loading (query/loading? state team-query)]
    (app-frame/app-frame
      state
      (if loading
        [:div
         {:class ["flex" "items-center" "justify-center" "py-20"]}
         [:div {:class ["text-gray-400"]} "Loading..."]]
        [:div
         {}
         [:div
          {:class ["mb-6" "flex" "items-center" "justify-between"]}
          [:h1
           {:class ["text-2xl" "font-bold" "text-gray-900"]}
           "Team"]
          [:ui/a.link
           {:ui/location
            {:location/page-id :pages/team-add-user
             :location/params {}}
            :class ["inline-flex" "items-center" "gap-1.5" "px-4" "py-2"
                    "text-sm" "font-medium" "rounded-lg" "text-white"
                    "bg-indigo-600" "hover:bg-indigo-700"]}
           "Invite user"]]

         [:div
          {:class ["space-y-6"]}
          (section-card
            {:title "Team Name"
             :description "This name is visible to all team members"}
            [(team-name-section state)])
          (members-section state)
          (invitations-section state)
          (disconnect-warning-section state)]]))))

(def page
  {:page-id :pages/team
   :route [["ui" "team"]]
   :on-load (fn [_location]
              [[:ui/hide-sidebar-on-mobile]
               [:store/dissoc ::team-name-input]
               [:data/query {:query/kind :query/team}]
               [:data/query {:query/kind :query/campaign-eligible}]])
   :render #'render})
